/**
 * The project's timeline, and the box for leaving a note on it.
 *
 * ONE list, not two. The plan asks for an activity timeline and for notes and
 * discussion as separate lines, and both are this tab: a note is an `Activity`
 * of type "note", filed into the same table as the status changes, the completed
 * milestones, the billed hours and the signed contracts. There is no comment
 * thread elsewhere on the page to go looking for.
 *
 * The reason is what a second list would do to a reader. "The client wants the
 * launch moved to June" and the switch to `on_hold` an hour later are two halves
 * of one story, and a page that puts the sentence in one place and the event in
 * another sorts them by different clocks and shows neither in context. A
 * timeline is worth having only while it is a single sequence; splitting it by
 * who wrote the line - a person or the platform - is the one cut that ruins it.
 * The server side argues the same decision at length in
 * `api/src/modules/crm-projects/activity.routes.ts`, including which four
 * automatic events earn an entry and which tempting ones deliberately do not.
 *
 * The rows are drawn by `_shared/ActivityList`, the same component the client
 * and juhiklient timelines use, because these are literally the same rows: an
 * entry filed against a project has to look like an entry, or an operator has to
 * learn the history twice. Nothing here reformats or re-sorts them.
 *
 * ## Paging is "show more", not pages
 *
 * A timeline is read from the top and hardly ever to the bottom, so a numbered
 * pager would be a control for a trip nobody makes. The newest page arrives with
 * the tab and each press appends the next one underneath, which keeps the entry
 * the operator was reading in place - the thing a page swap cannot promise.
 */
import { useCallback, useEffect, useState } from "react";
import { History, Loader2, Send } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform } from "../../../lib/auth";
import type { Activity } from "../../../lib/clients";
import { addProjectNote, getProjectActivity } from "../../../lib/projectActivity";
import { useCrmUser } from "../../shell/CrmUser";
import { Btn, Card, CardHead, EmptyState, useToast } from "../../ui";
import { ActivityList } from "../_shared/ActivityList";

/** The note the composer will accept, matched to the endpoint's own ceiling so a
 *  sentence is refused where it is typed rather than after the round trip. */
const NOTE_MAX = 10000;

/**
 * A card body's padding.
 *
 * `crm-card` is a surface and nothing else - the radius and the shadow, no
 * padding - and `crm-card-head` brings only its own. Everything else in a card
 * has to say where its edges are, or it runs the full width and the 28px corner
 * radius clips the first and last rows of whatever is inside. The same constant
 * the neighbouring cards declare for themselves (`EntityFiles`, `TaskChecklist`);
 * the linked-register tabs get it from `.crm-rows` instead, which is the other
 * half of the same rule.
 */
const BODY = "px-6 pb-5 pt-1";

export function ProjectActivity({
  projectId,
  onChange,
}: {
  projectId: string;
  /** Tells the page frame that the number on this tab's pill is now stale. */
  onChange?: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const canManage = userCanPlatform(useCrmUser(), "manage:crm.projects");
  // Null while the first read is in flight, so the spinner and the empty card
  // are two states and not one - "nothing has happened here" is the normal
  // answer for a new project, and it must not be confusable with a read that
  // failed on the way.
  const [rows, setRows] = useState<Activity[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getProjectActivity(projectId);
      setRows(res.rows);
      setTotal(res.total);
      setPage(res.page);
    } catch {
      // Said out loud, because the empty card cannot say it: "nothing has
      // happened on this project yet" is exactly what a failed read would draw,
      // and it is also the true answer for most projects today - so nobody would
      // ever think to doubt it.
      setRows([]);
      setTotal(0);
      toast(t("common.error"));
    }
  }, [projectId, toast, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The next page, appended, minus anything already on screen.
   *
   * Offset paging over a table that is still being written shifts under itself:
   * an entry filed after the first page was read pushes everything down one
   * place, so the row at offset 50 is the one already sitting at the bottom of
   * what the reader has. The offset window never LOSES an entry that way, only
   * repeats one, which is the right way round - but a repeat is not free here.
   * These rows are keyed by id, so a duplicate is a duplicate React key as well
   * as an event the timeline appears to have recorded twice, which is the one
   * thing a history must not say.
   *
   * And this is not a rare race. Posting a note prepends a row locally and adds
   * one on the server, so EVERY "show more" after a note would repeat the last
   * entry of the page above it. Filtering by id costs one Set per press and
   * leaves the sequence contiguous; the alternative is a cursor on the endpoint,
   * which is a bigger change than the fault deserves.
   */
  async function showMore() {
    setMore(true);
    try {
      const res = await getProjectActivity(projectId, page + 1);
      setRows((prev) => {
        const seen = new Set((prev ?? []).map((r) => r.id));
        return [...(prev ?? []), ...res.rows.filter((r) => !seen.has(r.id))];
      });
      setTotal(res.total);
      setPage(res.page);
    } catch {
      toast(t("common.error"));
    } finally {
      setMore(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const body = note.trim();
    if (!body) return;
    setBusy(true);
    try {
      const created = await addProjectNote(projectId, body);
      // Put on top of the list rather than refetched. The note is the newest
      // entry by definition, and a reload would throw away the pages the
      // operator has already opened underneath it - which on a long timeline is
      // the reading position they were writing about.
      setRows((prev) => [created, ...(prev ?? [])]);
      setTotal((n) => n + 1);
      setNote("");
      toast(t("projects.notePosted"));
      // The pill above this card counts the same rows, out of the summary
      // response. It is not recomputed from this list, so a note that did not
      // say so would leave a number over a card that disagrees with it for the
      // rest of the session.
      onChange?.();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHead title={t("projects.activity")} />

      <div className={BODY}>
        {canManage ? (
          <form onSubmit={submit} className="mb-4 space-y-2">
            <textarea
              className="crm-area"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("projects.activityNotePlaceholder")}
              maxLength={NOTE_MAX}
              rows={3}
            />
            <div className="flex justify-end">
              <Btn small tone="brand" type="submit" disabled={busy || !note.trim()}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                {t("projects.addNote")}
              </Btn>
            </div>
          </form>
        ) : null}

        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          // The empty card carries no action button: unlike the linked registers,
          // there is nothing to attach here - the way to fill this list is to do
          // the work, and the composer above is already on screen for anyone
          // allowed to write.
          <EmptyState icon={History} title={t("projects.activity")} body={t("projects.noActivity")} />
        ) : (
          <>
            <ActivityList rows={rows} empty={t("projects.noActivity")} />
            {rows.length < total ? (
              <div className="mt-4 flex justify-center">
                <Btn small onClick={() => void showMore()} disabled={more}>
                  {more ? <Loader2 className="size-4 animate-spin" /> : null}
                  {t("common.more")}
                </Btn>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Card>
  );
}
